import React from 'react';
import PolicyLayout from './PolicyLayout';
import useDocumentMeta from '../hooks/useDocumentMeta';

const LAST_UPDATED = '2026-06-02';

const PrivacyPolicy = () => {
  useDocumentMeta({
    title: 'Privacy Policy — Factory OS',
    description:
      'How Factory OS collects, uses, stores, and protects information you provide through factory-os.com and the Factory OS platform — including uploaded CAD files, contact information, and account data.',
    path: '/privacy',
  });

  return (
    <PolicyLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      lead="This policy explains what information Factory OS collects, how we use it, how we store it, and the choices you have. We try to keep this short and plain. If anything is unclear, please contact us."
    >
      <h2>1. Who this policy covers</h2>
      <p>
        This policy applies to the Factory OS marketing website at <code>factory-os.com</code> and to the Factory OS platform at <code>app.factory-os.com</code> and <code>demo.factory-os.com</code>. Where the platform behaves differently from the marketing site, we say so.
      </p>
      <p>
        &quot;Factory OS,&quot; &quot;we,&quot; &quot;our,&quot; and &quot;us&quot; refer to Factory OS. &quot;You&quot; refers to anyone visiting the website or using the platform.
      </p>

      <h2>2. Information we collect</h2>

      <h3>Information you give us directly</h3>
      <ul>
        <li>
          <strong>Early access &amp; contact forms.</strong> When you request early access, claim a voucher, or send us a message, we collect what you submit — typically your name, role, email, organization, team name, phone number (optional), a secondary contact (optional), and any voucher code you enter.
        </li>
        <li>
          <strong>Account information.</strong> If you create an account on the platform, we collect your email and any profile details you choose to add.
        </li>
        <li>
          <strong>Communications.</strong> If you email us, we keep that message and our reply so we can follow up.
        </li>
      </ul>

      <h3>Files you upload to the platform</h3>
      <p>
        On the Factory OS platform you may upload CAD models, drawings (DXF, DWG), PDFs, photos, scan data, test data, bills of materials, and other engineering artifacts attached to a design node.
      </p>
      <p>
        These files are stored so you and the people you authorize on your team can use them inside the platform.
      </p>

      <h3>Automatically collected information</h3>
      <ul>
        <li>
          <strong>Basic request data.</strong> Like most web services, our servers log standard request information (IP address, browser type, referring page, timestamp) for security, debugging, and abuse prevention.
        </li>
        <li>
          <strong>Visitor presence.</strong> We use <a href="https://pusher.com/" rel="noopener noreferrer">Pusher</a> to show real-time visitor counts on internal dashboards. We do not use this to build behavioral profiles of individual visitors.
        </li>
        <li>
          <strong>Website analytics.</strong> On our public marketing pages we use <a href="https://marketingplatform.google.com/about/analytics/" rel="noopener noreferrer">Google Analytics 4</a> to understand which pages people find useful — pages viewed, referring site, approximate location (city-level, derived from a truncated IP address that Google does not store), and general device and browser type. We do not send Google your name, email address, or anything you create in the platform, and we do not use analytics to build advertising profiles. You can opt out for every site you visit with Google's <a href="https://tools.google.com/dlpage/gaoptout" rel="noopener noreferrer">browser add-on</a>, or by using your browser's tracking protection. We run analytics on our public marketing pages only — it is switched off for the pages inside the signed-in product, so the work you do in Factory OS is never reported to Google.
        </li>
        <li>
          <strong>Cookies.</strong> We use the small number of cookies required to keep you signed in to the platform and to remember basic UI preferences, plus first-party Google Analytics cookies on our marketing pages that distinguish one visitor from another. We do not load advertising cookies, and we do not permit cross-site tracking for advertising purposes. If that changes, we will update this policy and ask for consent where required.
        </li>
      </ul>

      <h2>3. How we use the information</h2>
      <ul>
        <li>To provide and operate the platform — render your design nodes, run DFM checks, generate quote estimates, store test data, and produce reports.</li>
        <li>To respond to your contact form, voucher claim, or early access request.</li>
        <li>To send service-related emails (e.g., a manufacturing run status, password reset, security notice). We will not send marketing emails to an address you only used for support without your consent.</li>
        <li>To improve the platform — diagnose bugs, measure performance, and prioritize features.</li>
        <li>To meet legal obligations and protect Factory OS and our users from fraud, abuse, or harm.</li>
      </ul>

      <h2>4. Your uploaded files, your IP</h2>
      <div className="callout">
        <p>
          <strong>You own your files.</strong> Factory OS does not claim ownership of the CAD models, drawings, scans, photos, or other engineering data you upload.
        </p>
        <p>
          <strong>We do not use your files to train models, build a catalog of parts, or generate any internal asset without your explicit, written permission.</strong> Files you upload are stored to serve <em>your</em> design cycle and your team — not to feed any internal library.
        </p>
      </div>
      <p>
        We process your files only as needed to deliver the features you use (e.g., displaying a model, running a DFM check, generating a quote, producing a drawing package, or fabricating a part you specifically requested). When you delete a file or a project, we remove it from active storage on the schedule described in Section 7.
      </p>

      <h2>5. How we share information</h2>
      <p>
        We do not sell your personal information. We share information only in the limited situations below:
      </p>
      <ul>
        <li>
          <strong>Service providers.</strong> We use a small number of vendors to operate the service (hosting, database, email delivery, real-time presence). Each vendor only receives the data they need to do their job and is contractually required to protect it.
        </li>
        <li>
          <strong>Manufacturing partners.</strong> When you request fabrication of a part through the Factory OS shop, the design files and order details required to make the part are shared with the relevant production process owner. They are not shared with anyone else.
        </li>
        <li>
          <strong>Your team and people you authorize.</strong> Data you save to a project is visible to teammates with access to that project. You control who that is.
        </li>
        <li>
          <strong>Legal requests and safety.</strong> We may disclose information when required by valid legal process or when reasonably necessary to protect the rights, property, or safety of Factory OS, our users, or the public.
        </li>
        <li>
          <strong>Business transfers.</strong> If Factory OS is involved in a merger, acquisition, or asset sale, your information may transfer as part of that transaction. We will notify you and update this policy.
        </li>
      </ul>

      <h2>6. Payments</h2>
      <p>
        If we add paid plans or charge for manufacturing services, payments will be handled by a third-party payment processor (such as Stripe). We do not store full credit card numbers on our servers. The processor receives the payment details directly and only shares with us the limited information we need to fulfill your order (e.g., status, last four digits, billing address). Their terms and privacy policy apply to that transaction.
      </p>

      <h2>7. How long we keep information</h2>
      <ul>
        <li><strong>Account data:</strong> kept while your account is active, and for a reasonable period after closure for legal, accounting, or dispute-resolution needs.</li>
        <li><strong>Uploaded files and project data:</strong> kept while the project exists. When you delete a file or project, we remove it from active systems; routine backups containing that data are overwritten on a rolling schedule.</li>
        <li><strong>Contact-form and early-access submissions:</strong> kept until you ask us to delete them or for a reasonable period needed to follow up.</li>
        <li><strong>Server logs:</strong> typically kept for a short retention window for security and debugging.</li>
      </ul>

      <h2>8. Your rights and choices</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, export, restrict, or delete the personal information we hold about you, and to object to certain processing. You can exercise these rights at any time by contacting <a href="/contact">our privacy contact</a>.
      </p>
      <p>
        If you are in the European Economic Area, the United Kingdom, or California, you also have specific rights under GDPR, the UK GDPR, and the CCPA/CPRA respectively, including the right to lodge a complaint with your local supervisory authority.
      </p>
      <p>
        We will respond to verifiable requests within the timeframe required by applicable law.
      </p>

      <h2>9. Security</h2>
      <p>
        We use reasonable technical and organizational measures to protect information against loss, misuse, and unauthorized access. No system is perfectly secure; if we ever learn of a breach that affects your information, we will notify you in line with applicable law.
      </p>

      <h2>10. Children</h2>
      <p>
        Factory OS is intended for engineering teams, not children. We do not knowingly collect personal information from anyone under 13 (or the equivalent minimum age in your jurisdiction). If you believe we have, please contact us and we will delete it.
      </p>

      <h2>11. International users</h2>
      <p>
        Factory OS is operated from the United States. If you use the service from another country, your information will be transferred to and processed in that country. By using the service you understand this transfer.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We will update this policy from time to time. When we make a material change, we will update the &quot;Last updated&quot; date at the top of the page and, where appropriate, notify you through the platform or by email.
      </p>

      <h2>13. Contact us</h2>
      <p>
        For privacy questions, data-access requests, or to report a concern, contact us at <a href="/contact">our contact page</a> or write to:
      </p>
      <p>
        Factory OS · privacy@factory-os.com
      </p>
    </PolicyLayout>
  );
};

export default PrivacyPolicy;
